/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
*/

import {
    EmployeeViewType,
    EmployeeViewTypes,
    IScheduleFiltersEntity,
    ScheduleCalendarType,
    ScheduleOverViewType,
    ShiftFilterType,
    ShiftFilterTypes,
    UserStorageKeys
    } from "sh-models";
import { MemberDataService, ScheduleFiltersDataService, TagDataService, UserStorageService } from "sh-services";

import { clearFilteredMembers } from "../mutators/filter/clearFilteredMembers";
import clearFilteredTags from "../mutators/filter/clearFilteredTags";
import { defaultScheduleCalendarType, defaultScheduleOverviewType } from "../constants/ScheduleDefaults";
import { setMemberFilter } from "../mutators/setMemberFilter";
import { setSelectedShiftFilter } from "../mutators/setSelectedShiftFilter";
import { setShowProfilePicturesView } from "../mutators/setShowProfilePicturesView";
import { setStaffPerIntervalEnabled } from "../mutators/setStaffPerIntervalEnabled";
import { setTagFilter } from "../mutators/setTagFilter";
import { SchedulesViewStateStore } from "../store/schema/SchedulesViewStateStore";
import updateScheduleViewDimensions from "../actions/updateScheduleViewDimensions";
import UrlFactory from "../../../../utility/UrlFactory";
import MemberUtils from "../../../../utility/MemberUtils";
import { TeamStore } from "sh-team-store";
import * as moment from "moment";
import { Moment } from "moment";

const DEFAULT_GROUPED_STATE: boolean = true;

/**
 * The single authoritative initializer for the schedule view.
 *
 * It resolves the initial view (from the team's persisted schedule filters, deep-link overrides, or
 * admin-based defaults), dispatches the schedule view dimensions exactly once, and then applies the
 * team's persisted ancillary (client-side) filters. Consolidating this into one function guarantees the
 * store holds the correct view before any schedule data fetch is dispatched, so exactly one correct fetch
 * fires - avoiding the bootstrap-default vs restored-view double dispatch that raced on `viewState.shiftsInView`
 * (ICM 829740472).
 *
 * @param viewState The schedules view state store.
 * @param teamId The team id.
 */
export const initializeScheduleViewAndFilters = async (viewState: SchedulesViewStateStore, teamId: string): Promise<void> => {
    // Persisted filters are read from cache only (memory -> IndexedDB, no network), so awaiting this
    // before the first fetch does not delay render start. Fall back to defaults on any miss/error.
    let scheduleFilters: IScheduleFiltersEntity = null;
    try {
        scheduleFilters = await ScheduleFiltersDataService.getScheduleFilters(teamId);
    } catch (e) {
        scheduleFilters = null;
    }

    // Deep-link overrides (parsed once) take precedence over persisted calendar type / selected date.
    let deepLinkCalendarType: ScheduleCalendarType = null;
    let deepLinkSelectedDate: Moment = null;
    if (UrlFactory.isSchedulesPageDeepLink(window.location)) {
        deepLinkCalendarType = UrlFactory.getScheduleTypeFromDeepLink(window.location);
        deepLinkSelectedDate = UrlFactory.getScheduleSelectedDateFromDeepLink(window.location);
    }

    // Resolve every fetch-affecting view dimension for BOTH the persisted and the default (brand-new user)
    // cases so the single dispatch below carries the correct view and never needs a follow-up correction
    // (which would re-open the race window).
    const currentUser = TeamStore() && TeamStore().me;
    const isMemberAdmin = MemberUtils.isAdmin(currentUser);

    const employeeViewType: EmployeeViewType = scheduleFilters?.view?.quickAccess
        ?? (isMemberAdmin ? EmployeeViewTypes.TeamShiftsView : EmployeeViewTypes.YourShiftsView);
    // Fortnight kill-switch is intentionally NOT applied here - the orchestrator coerces Fortnight -> Week internally.
    const scheduleCalendarType: ScheduleCalendarType = deepLinkCalendarType ?? scheduleFilters?.scheduleView ?? defaultScheduleCalendarType;
    const scheduleOverViewType: ScheduleOverViewType = scheduleFilters?.view?.viewBy ?? defaultScheduleOverviewType;
    const isViewGrouped: boolean = scheduleFilters?.view?.show?.groups ?? getIsViewGroupedUserSetting();
    const selectedDate: Moment = deepLinkSelectedDate ?? moment();

    // Always clear existing group/member filters so stale filters never carry across a team switch (or a switch
    // to a team with no persisted filters). Clearing already-empty maps is a no-op, and doing it unconditionally
    // keeps this initializer self-contained rather than relying on resetTeamData ordering. Persisted filters (if
    // any) are re-applied after the dispatch below.
    clearFilteredTags();
    clearFilteredMembers();

    /** updateScheduleViewDimensions is used instead of updating viewState / SchedulesViewStateStore() directly,
     * because updating them on their own will not trigger a re-render for the schedule grid component.
     * updateScheduleViewDimensions() updates viewState and triggers a re-render.
     *
     * This is the single bootstrap dispatch (isTeamBootstrap = true). Because it now performs the actual load
     * for both small and large teams, instrumentViewedData is passed as `true` - the union of the previously
     * split instrumentation (bootstrap-default vs restored-view), so viewed data is instrumented exactly once. */
    updateScheduleViewDimensions(
        viewState,
        true, /* isTeamBootstrap */
        employeeViewType,
        scheduleCalendarType,
        scheduleOverViewType,
        selectedDate,
        isViewGrouped,
        true /* instrumentViewedData */
    );

    // Apply the persisted ancillary (client-side) filters AFTER the dispatch. These do NOT scope the fetch
    // (the API returns all team data in range; filtering is client-side), so they are safe to apply after.
    if (scheduleFilters) {
        const { filters, view } = scheduleFilters;
        const { groups, members, shifts } = filters;
        const { show } = view;

        setShowProfilePicturesView(show.showProfilePictures ?? true);
        setStaffPerIntervalEnabled({ value: show.staffPerTimeInterval });

        // set the selected groups
        const allGroups = await TagDataService.getTags(teamId);
        allGroups.forEach(group => {
            if (groups?.includes(group.id)) {
                setTagFilter(group, true);
            }
        });

        // set the selected members
        await Promise.all((members ?? []).map(async memberId => {
            try {
                const member = await MemberDataService.getMemberByIdFromCurrentTeam(memberId);

                if (member) {
                    setMemberFilter(member, true);
                }
            } catch (e) {
                // Ignore individual member lookup failures so initialization still completes.
            }
        }));

        // set the selected shift filters
        const shiftFilters = new Set<ShiftFilterType>(shifts ?? []);
        setSelectedShiftFilter(ShiftFilterTypes.ActiveShifts, shiftFilters.has(ShiftFilterTypes.ActiveShifts));
        setSelectedShiftFilter(ShiftFilterTypes.ConflictingShifts, shiftFilters.has(ShiftFilterTypes.ConflictingShifts));
        setSelectedShiftFilter(ShiftFilterTypes.TimeoffShifts, shiftFilters.has(ShiftFilterTypes.TimeoffShifts));
    }
};

/**
 * Returns the current value for the Grouped View user setting.
 */
function getIsViewGroupedUserSetting(): boolean {
    return UserStorageService.hasItem(UserStorageKeys.ScheduleGrouped) ? UserStorageService.getItem<boolean>(UserStorageKeys.ScheduleGrouped) : DEFAULT_GROUPED_STATE;
}
